import { useDispatch, useSelector } from "react-redux";
import {
  matchPath,
  Redirect,
  useHistory,
  useLocation,
  useRouteMatch,
} from "react-router-dom";
import {
  getRouteWithoutPath,
  isOnboardingPath,
  useQueryGroupId,
} from "utils/routes";
import React, {
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  ACCOUNT_EXTRA_DETAILS_PATH,
  BACKTESTING_EXTRA_DETAILS_PATH,
  BASE_BILLING_SETTINGS_PATH,
  BASE_PURCHASE_SUBSCRIPTION_SETTINGS_PATH,
  BOT_EXTRA_DETAILS_PATH,
  BOTTOM_NAVIGATOR,
  BOTTOM_NAVIGATOR_HEIGHT,
  COIN_EXTRA_DETAILS_PATH,
  GROUP_CONFIGURATION_PATH,
  INIT_DATA_QUERY_PARAM,
  IS_TELEGRAM_WEB_APP,
  LOCAL_STORAGE_TELEGRAM_INIT_DATA_KEY,
  LOCAL_STORAGE_TELEGRAM_QUICK_CONNECT_KEY,
  MAIN_CONTENT_ID,
  MARKETPLACE_GROUP_PATH,
  NOT_GROUP_MEMBER_MESSAGE,
  ONBOARDING_CONNECT_EXTERNAL_ACCOUNT_PATH,
  ONBOARDING_DCA_BOT_OVERVIEW_PATH,
  ONBOARDING_SIGNALS_GROUP_OVERVIEW_PATH,
  PAGE_SPINNER_MARGIN_FROM_TOP,
  PURCHASE_SUBSCRIPTION_SETTINGS_PATH,
  QUICK_CONNECT_DATA_QUERY_PARAM,
  SIGNAL_EXTRA_DETAILS_PATH,
  TELEGRAM_WEB_APP,
  TG_START_QUERY_PARAM,
  TRADE_EXTRA_DETAILS_PATH,
  UPGRADE_SUBSCRIPTION_SETTINGS_PATH,
} from "common/constants";
import useRoutesMatch from "utils/hooks/useRoutesMatch";
import { useNetworkState } from "react-use";
import { PageWrapperContext } from "common/Home/PageWrapper.context";
import {
  useLiveGroupSearch,
  usePrevious,
  useQueryWithRetries,
  useUser,
} from "@shared/utils/hooks/misc";
import useQuery from "utils/hooks/useQuery";
import {
  displayResultMessage,
  openMessage,
  redirectWithTelegramWebData,
} from "utils/misc";
import {
  ApiTags,
  FilterType,
  Inset,
  SubscriptionPlanType,
  TelegramWebappQueryData,
  WebAppUser,
  WorkspaceType,
} from "@shared/types";
import { useCheckQuotaUsage } from "@shared/features/settings/hooks";
import LoadingSpinnerOverlay from "common/Overlays/LoadingSpinnerOverlay";
import { IS_RESTRICTED_COUNTRY } from "utils/hooks/useRestrictedCountries";
import localStorageService from "utils/localStorageService";
import { PageLayout } from "types";
import { FILTER_TYPES_DATA } from "@shared/features/filters/constants";
import { SUBSCRIPTION_STEPS } from "@shared/features/settings/subscription/types";
import { SUBSCRIPTION_QUERY_PARAMS } from "@shared/features/settings/subscription/constants";
import { getParsedQueryPlan } from "@shared/features/settings/subscription/utils";
import useHistoryWithQuery from "utils/hooks/useHistoryWithQuery";
import { sharedLib } from "@shared/services/shared.service";
import useMedia from "utils/hooks/useMedia";
import { difference } from "lodash";
import useDetectKeyboardOpen from "utils/hooks/useDetectKeyboardOpen";
import { getSignalsFiltersSlice } from "@shared/features/signals/common/utils";
import { selectWorkspace } from "@shared/slices/common.slice";
import { AvoContext } from "analytics/AvoProvider";
import * as Sentry from "@sentry/react";

export const useIsOnboarding = () => {
  const user = useSelector(sharedLib.selectors.authentication.selectUser);
  const location = useLocation();

  return !user || user?.is_onboarding || isOnboardingPath(location);
};

export const scrollToTop = () => {
  const scrollingElement = document.getElementById(MAIN_CONTENT_ID);
  if (scrollingElement && scrollingElement.scrollTop >= 1) {
    scrollingElement.scroll(0, 0);
  }
};

export const useScrollToTop = () => {
  const location = useLocation();
  const DRAWERS_PATHS = [
    COIN_EXTRA_DETAILS_PATH,
    ACCOUNT_EXTRA_DETAILS_PATH,
    TRADE_EXTRA_DETAILS_PATH,
    BOT_EXTRA_DETAILS_PATH,
    SIGNAL_EXTRA_DETAILS_PATH,
    GROUP_CONFIGURATION_PATH,
    MARKETPLACE_GROUP_PATH,
    BACKTESTING_EXTRA_DETAILS_PATH,
    PURCHASE_SUBSCRIPTION_SETTINGS_PATH,
  ];
  const DELAY_SCROLLING_PATHS = [
    ONBOARDING_DCA_BOT_OVERVIEW_PATH,
    ONBOARDING_SIGNALS_GROUP_OVERVIEW_PATH,
    ONBOARDING_CONNECT_EXTERNAL_ACCOUNT_PATH,
  ];
  const isDrawerPath = Boolean(useRoutesMatch(DRAWERS_PATHS));
  const previousIsDrawerPath = useRef(isDrawerPath);
  const isDelayScrolling = Boolean(useRoutesMatch(DELAY_SCROLLING_PATHS));

  useEffect(() => {
    if (isDelayScrolling) {
      setTimeout(() => {
        scrollToTop();
      }, 300);
    } else if (!isDrawerPath && !previousIsDrawerPath.current) {
      scrollToTop();
    }
    previousIsDrawerPath.current = isDrawerPath;
  }, [location.pathname, isDrawerPath]);
};

export const useBaseQueries = () => {
  const user = useSelector(sharedLib.selectors.authentication.selectUser);

  const refetchIntervalMinutes = 10;
  const options = {
    pollingInterval: refetchIntervalMinutes * 60 * 1000,
    skip: user?.is_onboarding,
  };
  useQueryWithRetries(
    sharedLib.sharedApi.common.useGetPrecisionsQuery,
    undefined,
    options
  );
  useQueryWithRetries(
    sharedLib.sharedApi.common.useGetSymbolsMetadataQuery,
    undefined,
    options
  );
};

export const useOnReconnect = (onReconnect?: () => void) => {
  const networkState = useNetworkState();
  useEffect(() => {
    if (networkState.online && !networkState.previous) {
      try {
        onReconnect?.();
      } catch {}
    }
  }, [networkState]);
};

export const useHeaderSearchValue = (searchValue?: string) => {
  const { useMobileSearch: useHeaderSearch, search: headerSearch } =
    useContext(PageWrapperContext);

  return useHeaderSearch ? headerSearch.value : searchValue;
};

export const useHotjar = () => {
  const { user } = useUser(true);
  const [initializeUserId, setInitializedUserId] = useState<number>();

  useEffect(() => {
    if (user && user.id !== initializeUserId) {
      // @ts-ignore
      window.hj?.("identify", user.id, { user_id: user.id });
      // @ts-ignore
      setInitializedUserId(user.id);
    }
  }, [user]);
};

export const useSentryUser = () => {
  const { user } = useUser(true);
  const [initializeUserId, setInitializedUserId] = useState<number>();

  useEffect(() => {
    try {
      if (user && user.id !== initializeUserId) {
        Sentry.setUser({ id: user.id });
        setInitializedUserId(user.id);
      } else if (!user) {
        Sentry.setUser(null);
      }
    } catch {}
  }, [user]);
};

export const useDisplayLinkMessages = () => {
  const query = useQuery();
  const code = query.get("code");
  const message = query.get("message");

  useEffect(() => {
    if (code && code !== "200" && message) {
      openMessage(decodeURIComponent(message), "error");
    }
  }, [code, message]);
};

export const useRestrictedCountriesPurchase = () => {
  const history = useHistory();

  const onPurchaseClick = (nextPath: string) => {
    if (IS_RESTRICTED_COUNTRY) {
      openMessage(
        "Unfortunately, as stated in our Terms of Use, we do not currently provide services to users from your country.",
        "error"
      );
    } else history.push(nextPath);
  };

  return { onPurchaseClick };
};

export const useDrawerDelayedUnmount = (objectId?: number) => {
  const [localObjectId, setLocalObjectId] = useState(objectId);
  const [isUnmounting, setIsUnmounting] = useState(false);

  useEffect(() => {
    let clearLoadingTimeout: NodeJS.Timeout;
    if (objectId) {
      setLocalObjectId(objectId);
      setIsUnmounting(false);
    } else if (localObjectId) {
      setIsUnmounting(true);
      clearLoadingTimeout = setTimeout(() => {
        setLocalObjectId(undefined);
        setIsUnmounting(false);
      }, 1000);
    }
    return () => {
      if (clearLoadingTimeout) {
        clearTimeout(clearLoadingTimeout);
        setIsUnmounting(false);
      }
    };
  }, [objectId]);

  return { id: localObjectId, isUnmounting };
};

export const useDisablePageScroll = (disable = true) => {
  useEffect(() => {
    const mainContent = document.getElementById(MAIN_CONTENT_ID);
    if (disable && mainContent) {
      mainContent.scrollTop = 0;
      mainContent.style.overflowY = "hidden";
      mainContent.style.overflowY = "hidden";
      return () => {
        mainContent.style.overflowY = "auto";
      };
    }
  }, [disable]);
};

export const useCheckBillingRedirects = () => {
  const user = useSelector(sharedLib.selectors.authentication.selectUser);
  const query = useQuery();
  const queryPlan = getParsedQueryPlan(
    query.get(SUBSCRIPTION_QUERY_PARAMS.PLAN)
  ) as number;
  const queryStep = query.get(SUBSCRIPTION_QUERY_PARAMS.STEP);
  const isAllPlans = !!query.get(SUBSCRIPTION_QUERY_PARAMS.IS_ALL_PLANS);
  const checkQuotaImmediately =
    !!user &&
    !!queryPlan &&
    user.subscription_plan_type > queryPlan &&
    (!user?.isExpired() || user?.in_view_only_grace_period);
  const { isLoading, passedQuota } = useCheckQuotaUsage(
    checkQuotaImmediately,
    queryPlan as SubscriptionPlanType
  );
  if (!user) return;

  let message =
    "The URL you've tried to reach doesn't match your current subscription status";

  const testPath = (path: string, exact = true) =>
    !!matchPath(location.pathname, {
      path,
      exact,
      strict: false,
    });

  const isTrial = user.is_trial;
  const isExpired = user.isExpired();
  let redirectPath: string;
  if (isTrial) {
    redirectPath = BASE_PURCHASE_SUBSCRIPTION_SETTINGS_PATH;
    if (isAllPlans)
      redirectPath += `?${SUBSCRIPTION_QUERY_PARAMS.IS_ALL_PLANS}=1`;
  } else {
    redirectPath = BASE_BILLING_SETTINGS_PATH;
  }
  const isMaxSubscriptionPlan = user?.isPro();
  const isExtendPath = testPath(BASE_PURCHASE_SUBSCRIPTION_SETTINGS_PATH);

  const getNoMatchExtendPath = () => {
    if (!isExtendPath) return false;
    const noAutoRenewalMatch =
      user.has_auto_renewal &&
      (!isAllPlans ||
        queryPlan ||
        (queryStep && queryStep !== SUBSCRIPTION_STEPS.PLAN));
    if (noAutoRenewalMatch) return true;
    return (
      !isTrial &&
      !user.isFree() &&
      !isExpired &&
      !!queryPlan &&
      queryPlan > user.subscription_plan_type
    );
  };

  const noMatchExtendPath = getNoMatchExtendPath();
  const noMatchUpgradePath =
    testPath(UPGRADE_SUBSCRIPTION_SETTINGS_PATH) &&
    (isTrial ||
      isExpired ||
      isMaxSubscriptionPlan ||
      (!!queryPlan && user.subscription_plan_type >= queryPlan));
  const noMatchQuota = isExtendPath && passedQuota;

  if (noMatchExtendPath || noMatchUpgradePath || noMatchQuota) {
    if (noMatchExtendPath && user.has_auto_renewal) {
      message =
        "You currently have an active subscription with auto-renewal enabled. If you wish to purchase a different subscription plan, please disable your auto-renewal first and then proceed with your purchase.";
    }
    openMessage(message, "warning");
    return <Redirect to={redirectPath} />;
  } else if (isLoading) {
    return (
      <LoadingSpinnerOverlay
        isLoading={true}
        spinnerSize="large"
        spinnerMarginTop={PAGE_SPINNER_MARGIN_FROM_TOP}
      />
    );
  }
};

export const useLayoutChangeButtons = (storageKey: string) => {
  const defaultPageLayout =
    localStorageService.getParsedValue<PageLayout>(storageKey);
  const [isCardsView, setIsCardsView] = useState(
    !defaultPageLayout || defaultPageLayout === PageLayout.CARDS
  );

  const setIsCardsViewAndStorage = (isCardsView: boolean) => {
    setIsCardsView(isCardsView);
    localStorageService.setValue(
      storageKey,
      isCardsView ? PageLayout.CARDS : PageLayout.TABLE
    );
  };

  return {
    isCardsView,
    setIsCardsView: setIsCardsViewAndStorage,
  };
};

export const useNavigateToTradesPage = () => {
  const history = useHistory();

  const navigateWithQuery = (
    location: string,
    filterTypeToValue: { [key in FilterType]?: any }
  ) => {
    const updatedQuery = new URLSearchParams();
    Object.keys(filterTypeToValue).forEach((filterType) => {
      const value = filterTypeToValue[filterType];
      updatedQuery.set(
        FILTER_TYPES_DATA[FilterType.SOURCE_NAME].queryKey,
        JSON.stringify([value])
      );
    });
    history.push(`${location}?${updatedQuery.toString()}`);
  };

  return { navigateWithQuery };
};

export const useTelegramInitData = () => {
  const telegramInitData = (window as any)?.Telegram?.WebApp?.initData;
  const query = useQuery();
  let urlInitData = "";
  try {
    urlInitData = atob(query.get(INIT_DATA_QUERY_PARAM) || "");
  } catch {}

  const getTelegramUserInitData = (): WebAppUser | undefined => {
    try {
      const query = new URLSearchParams(telegramInitData);
      const userData = query.get("user") || "";
      return JSON.parse(userData);
    } catch {}
  };

  const initData = telegramInitData || urlInitData;

  return {
    initData,
    urlInitData,
    telegramInitData,
    getTelegramUserInitData,
  };
};

export const useTelegramWebRedirect = () => {
  const location = useLocation();
  const { initData: defaultInitData } = useTelegramInitData();
  const [getWebAppInitData, getWebAppInitDataStatus] =
    sharedLib.sharedApi.authentication.useLazyGetWebAppInitDataQuery();

  const redirectToWebsite = async (
    additionalParams?: Record<string, string>
  ) => {
    return getWebAppInitData()
      .unwrap()
      .then((initData) => {
        const _initData = initData ?? defaultInitData;
        if (_initData) {
          redirectWithTelegramWebData(
            _initData,
            additionalParams,
            location.pathname
          );
        }
        return _initData;
      });
  };

  return {
    redirectToWebsite,
    isLoading: getWebAppInitDataStatus.isLoading,
  };
};

export const useValidateTelegramWebData = () => {
  const { initData, urlInitData } = useTelegramInitData();
  const [validateWebAppInitData, validateInitDataStatus] =
    sharedLib.sharedApi.authentication.useValidateWebAppInitDataMutation();
  const { appendQuery } = useHistoryWithQuery();
  const telegramStartValue = useTelegramStartupQuery();

  const validateInitData = async () => {
    if (
      !initData ||
      localStorage.getItem(LOCAL_STORAGE_TELEGRAM_INIT_DATA_KEY) === initData
    ) {
      return Promise.reject();
    }

    localStorage.setItem(LOCAL_STORAGE_TELEGRAM_INIT_DATA_KEY, initData);
    return displayResultMessage(
      validateWebAppInitData({
        init_data: initData,
        ...telegramStartValue?.parameters,
      }).unwrap()
    ).finally(() => {
      if (urlInitData) {
        appendQuery(undefined, [INIT_DATA_QUERY_PARAM]);
      }
    });
  };

  return {
    validateInitData,
    validateInitDataStatus,
  };
};

const useDecodedUrlParam = (paramKey: string, parseJson?: boolean) => {
  const query = useQuery();
  const promoCode = query.get(paramKey);
  if (promoCode) {
    try {
      const parsedValue = atob(promoCode || "");
      if (parseJson) {
        return JSON.parse(parsedValue);
      }
      return parsedValue;
    } catch {}
  }
};

export const useTelegramStartupQuery = () => {
  const query = useQuery();

  const telegramStartValue = query.get(TG_START_QUERY_PARAM);
  return useMemo(() => {
    if (telegramStartValue) {
      try {
        return JSON.parse(atob(telegramStartValue.replaceAll("_", "=")));
      } catch (e) {}
    }
  }, [telegramStartValue]) as TelegramWebappQueryData | undefined;
};

export const useTelegramWebAppData = () => {
  const history = useHistory();
  const { setExtraLoginParams, setIsInitialized: setIsAvoInitialized } =
    useContext(AvoContext);
  const { initData } = useTelegramInitData();
  const [isInitialized, setIsInitialized] = useState(!initData);
  const { validateInitData, validateInitDataStatus } =
    useValidateTelegramWebData();
  const [getAuthUrl] =
    sharedLib.sharedApi.configuration.useLazyGetAuthUrlQuery();
  const quickConnectData = useDecodedUrlParam(
    QUICK_CONNECT_DATA_QUERY_PARAM,
    true
  );
  const telegramStartValue = useTelegramStartupQuery();
  const isNewUserRef = useRef<boolean | undefined>();

  const redirectToStartLocation = () => {
    if (telegramStartValue?.path) {
      history.push(telegramStartValue.path);
    }
  };

  useLayoutEffect(() => {
    if (IS_TELEGRAM_WEB_APP) {
      redirectToStartLocation();
      try {
        TELEGRAM_WEB_APP.ready?.();
        TELEGRAM_WEB_APP.expand?.();
        TELEGRAM_WEB_APP.disableVerticalSwipes?.();
        TELEGRAM_WEB_APP.requestFullscreen?.();
      } catch {}
    }
  }, [IS_TELEGRAM_WEB_APP]);

  useEffect(() => {
    if (validateInitDataStatus.isError && !isInitialized) {
      setIsInitialized(true);
    }
  }, [validateInitDataStatus.isError]);

  useEffect(() => {
    if (isInitialized) return;
    validateInitData()
      ?.then((response) => {
        if (quickConnectData) {
          getAuthUrl(quickConnectData)
            .unwrap()
            .then((response) => {
              localStorage.setItem(
                LOCAL_STORAGE_TELEGRAM_QUICK_CONNECT_KEY,
                performance.now().toString()
              );
              window.open(response, "_self");
            });
        } else {
          isNewUserRef.current = response?.data.is_new_user;
        }
      })
      .catch(() => {})
      .finally(() => {
        if (!quickConnectData) {
          setIsInitialized(true);
          setExtraLoginParams?.({
            ...telegramStartValue?.parameters,
            is_new_user: isNewUserRef.current,
          } as any);
          setIsAvoInitialized?.(true);
        }
      });
  }, [initData]);

  return {
    isInitialized,
    validateInitDataStatus,
    telegramStartValue,
  };
};

export const useBottomNavigator = () => {
  const isKeyboardOpen = useDetectKeyboardOpen();
  const baseShouldDisplay = useMedia(
    [`(min-width: ${BOTTOM_NAVIGATOR})`],
    [false],
    true
  );

  const { user } = useUser(true);
  const { safeAreaInset, contentSafeAreaInset } = useTelegramSafeAreaInsets();
  const workspace = useSelector(selectWorkspace);
  const shouldDisplay =
    baseShouldDisplay &&
    (!user?.is_onboarding || workspace === WorkspaceType.ADMIN);

  const height =
    shouldDisplay && !isKeyboardOpen
      ? (safeAreaInset.bottom ?? 0) +
        (contentSafeAreaInset.bottom ?? 0) +
        BOTTOM_NAVIGATOR_HEIGHT
      : 0;

  return {
    shouldDisplay,
    baseShouldDisplay,
    height,
  };
};

export const useIsWindowFocused = () => {
  const [focused, setFocused] = useState(!document.hidden);

  const onVisibilityChange = () => {
    setFocused(!document.hidden);
  };

  const onFocused = () => {
    setFocused(true);
  };

  const onBlur = () => {
    setFocused(false);
  };

  useEffect(() => {
    document.addEventListener("visibilitychange", onVisibilityChange);
    window.addEventListener("focus", onFocused);
    window.addEventListener("blur", onBlur);
    return () => {
      document.removeEventListener("visibilitychange", onVisibilityChange);
      window.removeEventListener("focus", onFocused);
      window.removeEventListener("blur", onBlur);
    };
  }, []);

  return focused;
};

export const usePollClientsList = () => {
  const [shouldPoll, setShouldPoll] = useState(false);
  const monitorStartTime = useRef<number | undefined>();
  const focused = useIsWindowFocused();
  const previousFocused = usePrevious(focused);
  const [initialAccountIds, setInitialAccountIds] = useState<
    number[] | undefined
  >();
  const [pollingAccountCreated, setPollingAccountCreated] = useState(false);
  const { data: accounts } =
    sharedLib.sharedApi.configuration.endpoints.getClientsList.useQueryState();
  const [getClientsList] =
    sharedLib.sharedApi.configuration.useLazyGetClientsListQuery();
  const dispatch = useDispatch();
  const isExpired =
    !!monitorStartTime.current &&
    performance.now() - monitorStartTime.current > 1000 * 60 * 10;

  const stopPolling = () => {
    setShouldPoll(false);
    monitorStartTime.current = undefined;
  };

  const refetchData = () => {
    getClientsList()
      .unwrap()
      .then((accountsList) => {
        const accountIds = accountsList?.map((account) => account.client_id);
        const accountsAdded = difference(
          accountIds,
          initialAccountIds || []
        ).length;
        if (accountsAdded) {
          setPollingAccountCreated(true);
          stopPolling();
          dispatch(
            sharedLib.sharedApi.authentication.util.invalidateTags([
              { type: ApiTags.USER, id: "LIST" },
            ])
          );
          dispatch(
            sharedLib.sharedApi.portfolio.util.invalidateTags([
              { type: ApiTags.PORTFOLIO_DATA, id: "LIST" },
            ])
          );
        }
      })
      .catch(() => {});
  };

  useEffect(() => {
    if (isExpired) {
      stopPolling();
    } else if (shouldPoll && focused && !previousFocused) {
      refetchData();
    }
  }, [shouldPoll, focused]);

  const startPolling = () => {
    monitorStartTime.current = performance.now();
    setInitialAccountIds(accounts?.map((account) => account.client_id) || []);
    setTimeout(() => setShouldPoll(true), 1000);
  };

  return {
    startPolling,
    pollingAccountCreated,
  };
};

export const useTelegramSafeAreaInsets = () => {
  const defaultValue = { top: 0, right: 0, bottom: 0, left: 0 };
  const [safeAreaInset, setSafeAreaInset] = useState<Inset>(
    TELEGRAM_WEB_APP?.safeAreaInset || defaultValue
  );
  const [contentSafeAreaInset, setContentSafeAreaInset] = useState<Inset>(
    TELEGRAM_WEB_APP?.contentSafeAreaInset || defaultValue
  );

  useEffect(() => {
    if (IS_TELEGRAM_WEB_APP) {
      TELEGRAM_WEB_APP?.onEvent("safeAreaChanged", () => {
        setSafeAreaInset(TELEGRAM_WEB_APP?.safeAreaInset);
      });
      TELEGRAM_WEB_APP?.onEvent("contentSafeAreaChanged", () => {
        setContentSafeAreaInset(TELEGRAM_WEB_APP?.contentSafeAreaInset);
      });
    }
  }, []);

  return {
    safeAreaInset,
    contentSafeAreaInset,
  };
};

interface useCheckUserMembershipProps {
  isAdminSignals?: boolean;
  isOpen?: boolean;
}
export const useCheckUserMembership = ({
  isAdminSignals,
  isOpen,
}: useCheckUserMembershipProps) => {
  const location = useLocation();
  const { historyPush } = useHistoryWithQuery();
  const dispatch = useDispatch();
  const querySubGroupId = useQueryGroupId();
  const [originalQuerySubGroupId] = useState(querySubGroupId);
  const filtersSlice = getSignalsFiltersSlice(isOpen, isAdminSignals);
  const loadedFilters = useSelector(
    filtersSlice.selectors.selectFilters
  ).loadedFilters;
  const groupsFilterData = useSelector(
    filtersSlice.selectors.selectFiltersData
  )[FilterType.GROUPS];
  const { isNotMember, isFetching, liveUpdateChannelsQuery } =
    useLiveGroupSearch(originalQuerySubGroupId);
  const isSignalExtraDetails = useRouteMatch(SIGNAL_EXTRA_DETAILS_PATH);

  useEffect(() => {
    if (isNotMember) {
      openMessage(NOT_GROUP_MEMBER_MESSAGE, "warning", 8);
      if (isSignalExtraDetails) {
        historyPush(
          getRouteWithoutPath(location.pathname, SIGNAL_EXTRA_DETAILS_PATH)
        );
      }
    }
  }, [isNotMember]);

  useEffect(() => {
    if (loadedFilters && isNotMember) {
      const defaultGroupId = groupsFilterData?.options[0];
      dispatch(filtersSlice.actions.setGroupsFilter([defaultGroupId]));
    }
  }, [isNotMember, loadedFilters]);

  return {
    isFetching,
    liveUpdateChannelsQuery,
  };
};

export const useVirtualKeyboard = () => {
  const [isKeyboardOpen, setKeyboardOpen] = useState(false);
  const [keyboardHeight, setKeyboardHeight] = useState(0);

  useEffect(() => {
    const visualViewport = window.visualViewport;

    if (visualViewport) {
      const onResize = () => {
        const focusedElement = document.activeElement as HTMLElement | null;

        // Bail if no element is focused as that also means no input is focused
        if (!focusedElement) return;

        const isInputFocused =
          focusedElement.tagName === "INPUT" ||
          focusedElement.tagName === "TEXTAREA";

        // Virtual keyboard should only be visible if an input is focused
        if (isInputFocused && visualViewport.height < window.innerHeight) {
          setKeyboardOpen(true);
          setKeyboardHeight(window.innerHeight - visualViewport.height);
        } else if (isKeyboardOpen) {
          // Reset keyboard height if it was open
          setKeyboardOpen(false);
          setKeyboardHeight(0);
        }
      };

      visualViewport.addEventListener("resize", onResize);

      return () => {
        visualViewport.removeEventListener("resize", onResize);
      };
    }
  }, [isKeyboardOpen]);

  return { keyboardHeight, isKeyboardOpen };
};
